import {
  Button,
  ContextMenu,
  Dialog,
  Icon,
  Li,
  Loading,
  Tooltip
} from '@startlibs/components';
import {getColor, isMobile, getFetcher, addQueryString} from '@startlibs/utils'
import {useLazyConstant, usePopupToggle, useToggle} from '@startlibs/core'
import ClipboardJS from 'clipboard'
import React, {useContext, useEffect, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'

import {CopyLinkComponent} from 'sharecomponents/src/pages/ShareDialog'

import {MarkAsReadDialog} from './markAsRead/MarkAsReadDialog'
import {StudyCard} from './StudyCard'
import {TOOLS_LABELS, TOOLS_ICONS} from '../../enums/Tools'
import {getInstantLink, useShareDialog} from './share/useShareDialog'
import {useAsyncDownload} from '../../hooks/useAsyncDownload'
import {useNotification, useNotificationRef} from '../../components/Notifications'
import {SuccessIcon} from '../../components/Styled'
import {FetchErrorDialog} from '../errors/FetchErrorDialog'
import { EditDicomDialog } from './EditDicomDialog';
import { ChangePatientDialog } from './ChangePatientDialog';
import { UnarchiveDialog } from './UnarchiveDialog';
import { darken, position, transparentize } from 'polished';
import { ARCHIVED, ARCHIVING, UNARCHIVED, UNARCHIVING, UNARCHIVING_FAILED } from '../../utils/utils';
import { ALL_CASES } from '../../enums/StudyStatus';
import { forEach } from 'lodash';
import { HasArchivedStudiesDialog } from './HasArchivedStudiesDialog';
import { UnarchiveFailedDialog } from './UnarchiveFailedDialog';
import { DicomRouteDialog } from './DicomRouteDialog';
import { DicomRouterContext } from '../../utils/DicomRouterContext';
import { ManageReportDialog } from './manageReport/ManageReportDialog';
import { DeleteStudyDialog } from './DeleteStudyDialog';

const ToolsCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  flex-wrap: wrap;
  > * {
    margin-left: 10px;
    :first-child {
      margin-left: 0;
    }
  }
`
const DropdownButton = styled(Icon)`
  ${Icon} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%,-50%);
    font-size: 14px;
  }
  color: ${getColor('secondary')};
  cursor: pointer;
  border-radius: 50%;
  display: block;
  background: rgba(41, 122, 163, 0.125);
  width: 2rem;
  height: 2rem;
  position: relative;
  flex-shrink: 0;
  :hover {
    background: rgba(41, 122, 163, 0.175);
  }
  }
`

const RouteButton = styled(Button)`
  color: ${getColor('secondary')};
  flex-shrink: 0;
  background: rgba(41, 122, 163, 0.125);
  box-shadow: none;
  font-weight: 600;
  :hover, :active {
    background: rgba(41, 122, 163, 0.175);
  }
`
const AddedLabel = styled.div `
  font-size: 13px;
  display: flex;
  align-items: center;
  color: ${getColor('secondary')};
  font-weight: 600;
  ${Icon} {
    font-size: 15px;
    margin-right: 0.3rem;
    font-weight: bold;
  }
`

const SelectToRouteDiv = styled.div`
  display: flex;
  align-items: center;
`	

export const ToolButton = styled(({href, badgeCount, badgeStatus, target = "_top", className, ...props}) => <Tooltip
  content={props.label}><a target={target} className={className} href={href}><Icon {...props}/></a></Tooltip>)`
  text-decoration: none;
  color: inherit;
  ${Icon} {
    font-size: 26px;
    color: ${getColor('secondary')};
  }
  ${props => (props.color || props.badgeCount) && css`
    :after {
      content: "${props => props.badgeCount ? props.badgeCount : ''}";
      font-weight: 600;
      color: white;
      font-size: 10px;
      min-width: 15px;
      height: 15px;
      line-height: 11px;
      text-align: center;
      position: absolute;
      bottom: 0;
      right: -5px;
      border-radius: 100px;
      background: ${getColor};
      border: 2px solid white;
      padding: 0 2px;
      white-space: nowrap;
      pointer-events: none;
      ${props => props.notReportOwner && css`
        background: transparent;
        box-shadow: inset 0 0 0 2px ${getColor};
      `}
    }
    ${props => props.notReportOwner && css`
      :before {
        content: '';
        width: 15px;
        height: 15px;
        position: absolute;
        bottom: 0;
        right: -5px;
        border-radius: 100px;
        background: white;
        pointer-events: none;
      }
    `}
    ${props => props.externalReport && css`
      :after, :before {
        display: none;
        pointer-events: none;
      }
    `}
  `}
`

const REPORT_STATUS_TO_COLOR = {
  "Draft": "warning",
  "Reported": "success"
}

const ToolStatus = styled.div`
  width: ${props => props.download ? '23' : props.downloadCD ? '20' : '24'}px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
`
const ToolLoading = styled(Loading)`
  flex-shrink: 0;
  margin-top: -2px;
`
const ErrorIcon = styled(Icon)`
  font-size: 20px;
  color: ${getColor('alert')};
`

const ArchivedDiv = styled.div`
  font-size: 12px;
  color: ${getColor('gray120')};
  margin-left: 0.5rem;
  margin-right: 0.25rem;
  padding-right: 1rem;
  display: flex;
  align-items: flex-end;
  position: relative;
  line-height: 1;
  flex-direction: column;
  justify-content: center;
  align-content: flex-end;
  min-height: 24px;
  margin-top: -1px;
  white-space: nowrap;
  :after {
    content: '';
    position: absolute;
    top: 0.25rem;
    bottom: 0.25rem;
    right: 0;
    border-right: 1px solid ${getColor('gray180')};
  }
  a {
    color: ${getColor('secondary')};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: inline-block;
    margin-top: 0.5rem;
    :hover {
      color: ${props => (darken(0.075, getColor('secondary')(props)))};
    }
    :active {
      color: ${props => (darken(0.1, getColor('secondary')(props)))};
    }
  }
`

const UnarchivingLabel = styled.div`
  display: flex;
  align-items: center;
  border-radius: 100px;
  padding: 5px 0.75rem 5px 5px;
  white-space: nowrap;
  background: ${props => (transparentize(0.87, getColor('main')(props)))};
  color: ${getColor('secondary')};
  font-weight: bold;
  ${Loading} {
    margin-right: 0.5rem;
  }
  cursor: pointer;
  :hover {
    background: ${props => (transparentize(0.82, getColor('main')(props)))};
  }
`

const UnarchivingFailedLabel = styled.div`
  display: flex;
  align-items: center;
  border-radius: 100px;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
  white-space: nowrap;
  background: ${props => (transparentize(0.87, getColor('alert')(props)))};
  color: ${getColor('alert')};
  font-weight: bold;
  cursor: pointer;
  :hover {
    background: ${props => (transparentize(0.82, getColor('alert')(props)))};
  }
  ${Icon} {
    font-size: 16px;
    margin-right: 0.25rem;
  }
`
const UnarchivedLabel = styled.div`
  margin-top: 0.75rem;
  flex-basis: 100%;
  > div {
    display: inline-flex;
    align-items: center;
    border-radius: 100px;
    padding: 0.25rem 1rem 0.25rem 0.75rem;
    white-space: nowrap;
    background: ${props => (transparentize(0.87, getColor('success')(props)))};
    color: ${getColor('success')};
    font-weight: bold;
  }
  ${Icon} {
    font-size: 16px;
    margin-right: 0.25rem;
  }
`

// Function to check if user has at least one permission that would require
// the dicom to be unarchived prior to perfoming the action
function hasDicomPermission(permissions, dicomPermissions) {
  for (const [permission, value] of permissions) {
    if (dicomPermissions.includes(permission)) {
      if (value >= 0) {
        return true;
      }
    }
  }
  return false;
}

// const isMarkAsReadDisabled = (status) => status !== "Draft" && status !== "Pending"
const canMarkAsRead = (status) => status == "Draft" || status == "Pending"
const canUnMarkAsRead = (status) => status == "Unknown"
const isMarkAsReadDisabled = (status) => status == "Reported"

const openLinkOnContextMenu = (url, component, notReportOwner) => component === Li ? (() => window.open(url, notReportOwner ? "_blank" : "_top")) : undefined

export const ToolsCell = ({study, refreshList, userRights, setStudy, isArchived, setParams, unarchivingPanel, setUnarchivingWorklist, setInvalidateCache, setUnarchivingTimeoutIds, usingUnarchiver, userLogin, setStudies, setCurrentQuantity}) => {

  const { 
    isSelectingStudiesToRoute, 
    selectedStudiesToRoute, 
    setSelectedStudiesToRoute, 
    setNumberOfSelectedStudies
  } = useContext(DicomRouterContext);
  const {tools} = study
  var toolsRights = _.toPairs(userRights.tools.functions)

  // Adjust to show the delete study button in the last position
  const index = toolsRights.findIndex(item => 
    Array.isArray(item) && item[0] === 'deleteStudy'
  );
  // If the item is found and it's not already the last item
  if (index !== -1 && index !== toolsRights.length - 1) {
      // Remove the item from the array
      const [item] = toolsRights.splice(index, 1);
      // Push the item to the end of the array
      toolsRights.push(item);
  }
  
  const dicomPermissions = ['viewStudy', 'viewerLink', 'launchInHoros', 'downloadStudy', 'downloadCD', 'report','share', 'dicomEdit', 'changePatient'];
  const canUnarchive = isArchived && hasDicomPermission(toolsRights, dicomPermissions);
  
  const visibleTools = toolsRights.filter(([, isVisible]) => isVisible === 1 && !isMobile())
  const notVisibleTools = toolsRights.filter(([name, isVisible]) => (name !== "reportWriteAccess") && ((isVisible === 0) || (isVisible === 1 && isMobile())))
  const contextMenu = isMobile ? usePopupToggle() : usePopupToggle()
  const [, setNotification] = useNotificationRef()

  const [openDialog, dialog] = useShareDialog(tools.shareStudyWeblink+'&images='+study.study.imageCount)
  const dicomEdit = useToggle()
  const changePatient = useToggle()
  const deleteStudy = useToggle()
  const unarchive = useToggle()
  const unarchiveFailed = useToggle()
  const hasArchivedStudies = useToggle()
  const dicomRouteDialogToggle = useToggle()

  const MILLISECONDS_CHECK_UNARCHIVE_STATUS = 8000

  const [markAsReadDialog, setMarkAsReadDialog] = useState()
  const [manageReportDialog, setManageReportDialog] = useState()
  const [instantLinkLoading, setInstantLinkLoading] = useState()
  const [instantShareDialog, setInstantShareDialog] = useState()

  const [startDownload, downloadLoading, downloadStarted] = useAsyncDownload(study, "regular")
  const [startDownloadCD, downloadLoadingCD, downloadStartedCD] = useAsyncDownload(study, "cd")
  const studyUid = study.study.iuid;
  
  useEffect(() => {
    let isMounted = true;
  
    if (study.study.rotate_status === UNARCHIVING) {
      const checkUnarchiveStatus = () => {
        getFetcher('/StudyRotateStatus.php?id_study=' + studyUid)
          .then((response) => {
            if (
              response.rotate_status === UNARCHIVED ||
              response.rotate_status === null
            ) {
              if (isMounted) { 
                setStudy((prevStudy) => ({
                  ...prevStudy,
                  study: {
                    ...prevStudy.study,
                    rotate_status: UNARCHIVED,
                    unarchivedRecently: true
                  },
                  
                }));
                //Update the unarchiving worklist setting study.rotate_status to UNARCHIVED
                setUnarchivingWorklist(
                  (prev) =>
                    prev.map((study) => {
                      if (study.study.iuid === studyUid) {
                        return {
                          ...study,
                          study: {
                            ...study.study,
                            rotate_status: UNARCHIVED,
                          },
                        };
                      }
                      return study;
                    })
                )
                setInvalidateCache(true)
              }
              return;
            } else if (response.rotate_status === ARCHIVED || response.rotate_status === UNARCHIVING_FAILED) {
              if (isMounted) { 
                setStudy((prevStudy) => ({
                  ...prevStudy,
                  study: {
                    ...prevStudy.study,
                    rotate_status: response.rotate_status,
                  },
                }));
                setInvalidateCache(true)
              }
              return;
            }
            if (isMounted) {
              const timeoutId = setTimeout(checkUnarchiveStatus, MILLISECONDS_CHECK_UNARCHIVE_STATUS);
              return timeoutId;
            }
          })
          .catch((e) => {
            console.log('error', e);
          });
      };
  
      const timeoutId = setTimeout(checkUnarchiveStatus, MILLISECONDS_CHECK_UNARCHIVE_STATUS);
      setUnarchivingTimeoutIds(list => [...list, timeoutId]);
  
      return () => {
        clearTimeout(timeoutId); // Clear the timeout in the cleanup function
        isMounted = false;
      };
    }
  }, [study.study.rotate_status]);
  
  const loadInstantLink = () => {
    setInstantShareDialog(true)
    setInstantLinkLoading(true)
    getInstantLink(tools.shareStudyWeblink)
      .then((link) => {
        setInstantShareDialog(link)
      })
      .catch((e) => {
        setInstantShareDialog({error: _.get('0.status', e) || true})
      })
      .finally(() => setInstantLinkLoading(false))
  }

  const reportCtxMenu = isMobile() ? useToggle() : usePopupToggle()

  // isMobile ? usePopupToggle() : usePopupToggle()

  useEffect(() => {
    if (!instantShareDialog) {
      setInstantLinkLoading(false)
    }
  }, [instantShareDialog])

  const isWriteAccessLegacy = _.isNil(userRights.tools.functions.reportWriteAccess)
  // const isSystemAdmin = userRights.tools.functions.isAdmin
  const isSystemAdmin = userRights.isAdmin

  const VISIBLE_TOOLS = ({
    "attachNonDicom": (links, isArchived, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links.attachNonDicom.weblink, Component)} label={TOOLS_LABELS['attachNonDicom']}
      key="attachNonDicom" color={links.attachNonDicom.content && "success"} badgeCount={links.attachNonDicom.content}
      icon="attachments" href={links.attachNonDicom.weblink}/>,

    "studyNotes": (links, isArchived, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links.studyNotes.weblink, Component)} disabled={!links.studyNotes.weblink}
      label={TOOLS_LABELS['studyNotes']} color={links.studyNotes.content && "success"} key="studyNotes"
      icon="study-notes" href={links.studyNotes.weblink}/>,

    "report": (links, isArchived, Component = ToolButton) => {

      if(isMobile()){
        reportCtxMenu.openWith(links.markAsReadWeblink)
      }

      const disabled = isWriteAccessLegacy
      ? ((links.report.reportStatus !== "Reported" && links.report.right !== "rw") || !links.report.weblink) || isArchived
      : !links.report.weblink || (isArchived && links.report.reportStatus !== "Reported")

      const notReportOwner = links.report.right !== "rw"

      const externalReport = links.report.right === "dw"

      const showManageReport = !externalReport && !isWriteAccessLegacy && isSystemAdmin && (links.report.reportStatus === "Reported" || links.report.reportStatus === "Draft")
      const manageReportDisabled = showManageReport && links.report.right === "rw" && study.study.dictated_by === ''

      return <Component onClick={
        () => {
          showManageReport 
            // ? reportCtxMenu.open()
            ? reportCtxMenu.openWith(links.markAsReadWeblink)
            : openLinkOnContextMenu(links.report.weblink, disabled ? null : Component,notReportOwner)
        }}
                        notReportOwner={notReportOwner}
                        externalReport={externalReport}
                        target={notReportOwner ? "_blank" : undefined}
                        disabled={disabled}
                        label={
                          externalReport ? "External report" :
                          isWriteAccessLegacy ? "Report" :
                          links.report.reportStatus === "Reported" ? "View report" :
                          links.report.reportStatus === "Draft" ? (notReportOwner ? "Report in progress" : "Edit report") :
                          links.report.right === "rw" ? "Create report" : "No report"                          
                        }
                        color={REPORT_STATUS_TO_COLOR[links.report.reportStatus]} key="report" icon={externalReport ? "external-report" : "report"}
                        href={
                          showManageReport || disabled || ((notReportOwner && Component === Li) && isWriteAccessLegacy) ? undefined : links.report.weblink
                        }
              >
                {/* <Button onClick={() => reportCtxMenu.openWith(links.markAsReadWeblink)}>op</Button> */}
                
                {(reportCtxMenu.isOpen == links.markAsReadWeblink || Component === Li) && <ContextMenu 
                // {reportCtxMenu.isOpen && <ContextMenu 
                  onCollision={isMobile() ? "fit flip" : undefined}
                  offset={isMobile() ? "6 -10" : "6 2"}
                >
                  <Li 
                    onClick={() => {
                      openLinkOnContextMenu(links.report.weblink, disabled ? null : Component,notReportOwner)                      
                    }}
                    label={
                      externalReport ? "External report" :
                      isWriteAccessLegacy ? "Report" :
                      links.report.reportStatus === "Reported" ? "View report" :
                      links.report.reportStatus === "Draft" ? (notReportOwner ? "Report in progress" : "Edit report") :
                      links.report.right === "rw" ? "Create report" : "No report"                          
                    } 
                    target={notReportOwner ? "_blank" : "_top"}
                    disabled={disabled}
                    icon={externalReport ? "external-report" : "report"}
                    href={
                      disabled || ((notReportOwner && Component === Li) && isWriteAccessLegacy) ? undefined : links.report.weblink
                    }
                  />
                  {showManageReport && <Li label="Unlock editing permission" icon="unlock-line" disabled={manageReportDisabled} onClick={() => setManageReportDialog(true)}/>}
                  {/* {showManageReport && <>{!isSystemAdmin ? <Tooltip content="Only system admins can manage reports">
                    <Li label="Manage report" icon={"edit"} disabled={!isSystemAdmin} onClick={() => setManageReportDialog(true)}/>
                  </Tooltip> : 
                    <Li label="Manage report" icon={"edit"} disabled={!isSystemAdmin} onClick={() => setManageReportDialog(true)}/>
                  }</>} */}
                  
                </ContextMenu>}
              
      </Component>
    },

    "share": (links, isArchived, Component = ToolButton) => !isArchived ? <Component key="share" label={TOOLS_LABELS['share']} icon="send-line"
                                                           onClick={openDialog}/> : null,

    "downloadStudy": (links, isArchived, Component = ToolButton) => downloadLoading ?
      <Tooltip content="Preparing download"><ToolStatus download><ToolLoading size={18}/></ToolStatus></Tooltip>
      : downloadStarted ?
        <Tooltip content="Download started"><ToolStatus download><SuccessIcon icon="check"/></ToolStatus></Tooltip>
        :
        !isArchived ? <Component key="downloadStudy" label={TOOLS_LABELS['downloadStudy']} icon={TOOLS_ICONS["downloadStudy"]}
                      href={links["downloadStudyWeblink"]}/> : null,

    "downloadCD": (links, isArchived, Component = ToolButton) => downloadLoadingCD ?
      <Tooltip content="Preparing download"><ToolStatus downloadCD><ToolLoading size={18}/></ToolStatus></Tooltip>
      : downloadStartedCD ?
        <Tooltip content="Download started"><ToolStatus downloadCD><SuccessIcon icon="check"/></ToolStatus></Tooltip>
        :
        !isArchived ? <Component key="downloadCD" label={TOOLS_LABELS['downloadCD']} icon={TOOLS_ICONS["downloadCD"]}
                      href={links["downloadCDWeblink"]}/> : null,

    "markAsRead": (links, isArchived, Component = ToolButton) => <Component 
                                                                label={canMarkAsRead(links.report.reportStatus) 
                                                                  ? "Mark as read" 
                                                                  : "Unmark as read"}
                                                                key="markAsRead" 
                                                                disabled={isMarkAsReadDisabled(links.report.reportStatus)}
                                                                icon={canMarkAsRead(links.report.reportStatus) 
                                                                  ? "unavailable"
                                                                  : "undo-unavailable"}
                                                                onClick={() => setMarkAsReadDialog(true)}/>,

    "viewerLink": (links, isArchived, Component = ToolButton) => !isArchived ? <Component label={TOOLS_LABELS["viewerLink"]} key="viewerLink"
                                                                icon={TOOLS_ICONS["viewerLink"]}
                                                                onClick={loadInstantLink}
                                                                isLoading={instantLinkLoading}/> :null,

    "viewStudy": (links, isArchived, Component = ToolButton) => !isArchived ?

      usingUnarchiver ?
        <Component
          // onClick={Component === ToolButton ? undefined : (
          onClick={
            usingUnarchiver ?
              () => getFetcher(addQueryString('/StudyWorklist.php?',
                { maxResults: 100, page: 1, studyStatus: ALL_CASES, patientId: study.patient.patientID }))
                .then(({ worklist }) => {
                  if (worklist.dbCount > 1) {
                    var countArchived = 0
                    forEach(worklist.rows, (study) => {
                      if (study.study.rotate_status === ARCHIVED || study.study.rotate_status === ARCHIVING || study.study.rotate_status === UNARCHIVING) {
                        countArchived++
                      }
                    })
                    if (countArchived > 0) {
                      return hasArchivedStudies.toggle()
                    }
                  }
                  window.open(links['viewStudyWeblink'], "_blank")
                }
                )
                .catch((e) => {
                  console.log(e)
                  window.open(links['viewStudyWeblink'], "_blank")
                })
              : () => window.open(links['viewStudyWeblink'], "_blank")
          }
          // href={Component !== ToolButton ? undefined : links['viewStudyWeblink']}
          key="viewStudy"
          label={TOOLS_LABELS["viewStudy"]} icon={TOOLS_ICONS["viewStudy"]} target='_blank' />
          : <Component
              onClick={Component === ToolButton ? undefined : (() => window.open(links['viewStudyWeblink'], "_blank"))}
              href={Component !== ToolButton ? undefined : links['viewStudyWeblink']} key="viewStudy"
              label={TOOLS_LABELS["viewStudy"]} icon={TOOLS_ICONS["viewStudy"]} target='_blank'/>
      : null,

    "dicomEdit": (links, isArchived, Component = ToolButton) => !isArchived ? <Component key="dicomEdit" label={TOOLS_LABELS['dicomEdit']} icon="edit-line"
      onClick={() => {dicomEdit.toggle()}}/> : null,
    
    "changePatient": (links, isArchived, Component = ToolButton) => !isArchived ? <Component key="changePatient" label={TOOLS_LABELS['changePatient']} icon="change-patient"
      onClick={() => {changePatient.toggle()}}/> : null,

    "dicomRoute": (links, isArchived, Component = ToolButton) => !isArchived ? <Component key="dicomRoute" label={TOOLS_LABELS['dicomRoute']} icon="route"
      onClick={() => {dicomRouteDialogToggle.toggle()}}/> : null,

    "launchInHoros": (links, isArchived, Component = ToolButton) => !isArchived ? <Component
      onClick={openLinkOnContextMenu(links['launchInHorosWeblink'], Component)} key="launchInHoros"
      label={TOOLS_LABELS["launchInHoros"]} icon={TOOLS_ICONS["launchInHoros"]} href={links['launchInHorosWeblink']}/> : null,

    "activityLogs": (links, isArchived, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links['activityLogsWeblink'], Component)} key="activityLogs"
      label={TOOLS_LABELS["activityLogs"]} icon={TOOLS_ICONS["activityLogs"]} href={links['activityLogsWeblink']}/>,

    "deleteStudy": (links, isArchived, Component = ToolButton) => <Component
      onClick={() => {deleteStudy.toggle()}} key="deleteStudy"
      label={TOOLS_LABELS["deleteStudy"]} icon={TOOLS_ICONS["deleteStudy"]} />,
  })
  
  const isLoadingContextMenu = instantLinkLoading && !userRights.tools.functions.viewerLink

  const downloadInContextMenu = !visibleTools.find(([tool]) => tool === "downloadStudy")
  const downloadCDInContextMenu = !visibleTools.find(([tool]) => tool === "downloadCD")



  return <ToolsCellContainer>
    {/* {isArchived && <> */}
    {isArchived && study.study.rotate_status !== UNARCHIVING && study.study.rotate_status !== UNARCHIVING_FAILED &&
      <ArchivedDiv>
        <div>Study archived</div>
        {canUnarchive && <a onClick={()=> {
          unarchive.toggle()
        }}>Unarchive</a>}
      </ArchivedDiv>
      }
    {/* {isArchived && study.isUnarchiving && <> */}
    {isArchived && study.study.rotate_status === UNARCHIVING && <>
      <Tooltip content="Unarchiving may take a few minutes">
        <UnarchivingLabel onClick={() => {
          unarchivingPanel.open()
          setUnarchivingWorklist(unarchivingWorklist => {
            unarchivingWorklist.push(study)
            // filter out duplicates
            return _.uniqBy('study.iuid', unarchivingWorklist)
          })
        }}>
          <Loading size={16} borderWidth={3}/>
          <span>Unarchiving</span>
        </UnarchivingLabel>
      </Tooltip>
    </>}

    {isArchived && study.study.rotate_status === UNARCHIVING_FAILED && <>
      <UnarchivingFailedLabel onClick={() => {
        unarchiveFailed.toggle()
      }}>
        <Icon icon="warning"/>
        <span>Unarchiving Failed</span>
      </UnarchivingFailedLabel>
    </>}
    {isSelectingStudiesToRoute && !isArchived
      ? selectedStudiesToRoute.findIndex(item => item?.study?.iuid === studyUid) >= 0
        ? <SelectToRouteDiv>
            <AddedLabel>
              <Icon icon="check"/>
              Added
            </AddedLabel>
            <RouteButton css="margin-left:1rem;" onlyIcon noFocus icon="x" small onClick={() => {
              setSelectedStudiesToRoute(selectedStudies => {
                const removableIndex = selectedStudies.findIndex(item => item?.study?.iuid === studyUid)
                if(removableIndex >= 0){
                  selectedStudies.splice(removableIndex, 1)
                }
                return selectedStudies
              })
              setNumberOfSelectedStudies(number => number - 1) // this is here to unsure re-render accros table and panel
            }}>
            </RouteButton>
          </SelectToRouteDiv>
        : <RouteButton noFocus icon="route" small onClick={() => {
            var array = selectedStudiesToRoute
            array.push(study)
            setSelectedStudiesToRoute(array)
            setNumberOfSelectedStudies(number => number + 1) // this is here to unsure re-render accros table and panel
          }}>
            Add to route
          </RouteButton>
      : null
    }
    {
      !isSelectingStudiesToRoute && visibleTools.map(([tool]) => VISIBLE_TOOLS[tool] && VISIBLE_TOOLS[tool](tools, isArchived))
    }
    {
      !isSelectingStudiesToRoute && notVisibleTools.length > 0 &&
      (
        ((downloadLoading && downloadInContextMenu) || (downloadLoadingCD && downloadCDInContextMenu))
          ? <Tooltip content="Preparing download"><ToolStatus><ToolLoading size={18}/></ToolStatus></Tooltip>
          : ((downloadStarted && downloadInContextMenu) || (downloadStartedCD && downloadCDInContextMenu))
          ? <Tooltip content="Download started"><ToolStatus><SuccessIcon icon="check"/></ToolStatus></Tooltip>
          : <DropdownButton isLoading={isLoadingContextMenu} onClick={contextMenu.open}><Icon icon="arrow-down"/>
            {
              contextMenu.isOpen &&
              <ContextMenu onCollision={isMobile() ? "fit flip" : undefined}>
                {notVisibleTools.map(([tool]) => VISIBLE_TOOLS[tool] && VISIBLE_TOOLS[tool](tools, isArchived, Li))}
                {isArchived && <div style={{
                  padding: '1.0rem 1.5rem',
                  color: '#9d9d9d',
                  borderTop: '1px solid #d2d2d2',
                  width: '160px'
                }}>Unarchive this study to enable all options</div>}
                {/* {isArchived && <ArchivedMsg>Unarchive this study to enable all options</ArchivedMsg>} */}
              </ContextMenu>
            }
          </DropdownButton>
      )
    }
    {
      study.study?.unarchivedRecently && <UnarchivedLabel><div><Icon icon="check" color="gray120"/>Unarchiving completed</div></UnarchivedLabel>
    }
    {unarchive.isOpen &&
      <UnarchiveDialog study={study} setStudy={setStudy} closeDialog={unarchive.close} unarchivingPanel={unarchivingPanel} setUnarchivingWorklist={setUnarchivingWorklist} setInvalidateCache={setInvalidateCache}/>
    }
    {unarchiveFailed.isOpen &&
      <UnarchiveFailedDialog study={study} closeDialog={unarchiveFailed.close}/>
    }
    {hasArchivedStudies.isOpen &&
      <HasArchivedStudiesDialog study={study} closeDialog={hasArchivedStudies.close} 
        viewer={tools.viewStudyWeblink} 
        setParams={setParams} 
      />
    }
    {dicomRouteDialogToggle.isOpen &&
      <DicomRouteDialog studyList={[study]} closeDialog={dicomRouteDialogToggle.close} />
    }
    {
      markAsReadDialog &&
      <MarkAsReadDialog refreshList={refreshList} study={study} closeDialog={() => setMarkAsReadDialog(false)}
                        url={tools.markAsReadWeblink}/>
    }
    {
      manageReportDialog &&
      <ManageReportDialog refreshList={refreshList} study={study} closeDialog={() => setManageReportDialog(false)}
                        url={tools.markAsReadWeblink}/>
    }
    {dialog}
    {dicomEdit.isOpen && 
      <EditDicomDialog study={study} setStudy={setStudy} closeDialog={dicomEdit.close} url={tools.editDicomWeblink} setInvalidateCache={setInvalidateCache}/>
    }
    {changePatient.isOpen && 
      <ChangePatientDialog study={study} setStudy={setStudy} closeDialog={changePatient.close} url={tools.editDicomWeblink} setInvalidateCache={setInvalidateCache}/>
    }
    {deleteStudy.isOpen &&
      <DeleteStudyDialog 
        study={study} 
        setStudy={setStudy} 
        closeDialog={deleteStudy.close} 
        url={tools.editDicomWeblink} 
        setInvalidateCache={setInvalidateCache}
        // hasAttachments={false}
        hasStudyNotes={tools.studyNotes.content}
        hasAttachments={tools.attachNonDicom.content}
        hasTags={study.study?.tags?.length > 0}
        hasReports={tools.report.reportStatus === "Reported" || tools.report.reportStatus === "Draft"}
        setStudies={setStudies}
        setCurrentQuantity={setCurrentQuantity}
      />
    }
    {
      instantShareDialog && !instantShareDialog.error &&
      <Dialog
        className="JS-copy-focus-container"
        title="Study images webviewer link"
        closeDialog={() => setInstantShareDialog(false)}
        footer={<>
          <Button onClick={() => setInstantShareDialog(false)}>Cancel</Button>
          <CopyButton highlight text={instantShareDialog} close={(msg) => {
            setNotification(msg);
            setInstantShareDialog(false)
          }} isLoading={instantLinkLoading}>Copy link & close</CopyButton>
        </>}
      >
        <StudyCard study={study}/>
        <CopyLinkComponent url={instantShareDialog} label="Use the link below to view study images in browser:"
                           key={instantShareDialog} loadingShareableLink={instantLinkLoading}/>
      </Dialog>
    }
    {
      instantShareDialog && instantShareDialog.error &&
      <FetchErrorDialog closeDialog={() => setInstantShareDialog(false)}
                        message="An error has occurred while trying to load the link. Please refresh the page and try again."/>

    }
  </ToolsCellContainer>
}

const CopyButton = ({text, close, isLoading}) => {
  const ref = useRef()
  useEffect(() => {
    const btn = new ClipboardJS('.copy-to-clipboard', {
      container: ref.current.closest('.JS-copy-focus-container')
    })
    return () => btn.destroy()
  }, [])

  return <Button
    buttonRef={ref}
    className="copy-to-clipboard"
    isLoading={isLoading}
    data-clipboard-text={text}
    highlight
    onClick={() => close(ClipboardJS.isSupported() ? 'Shareable link copied to clipboard' : "You've finished the share function")}
  >
    {ClipboardJS.isSupported() ? 'Copy link & Close' : 'Done'}
  </Button>
}
